function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useMemo, useContext } from 'react';
import { Box, FormField, ThemeContext } from 'grommet';
import styled from 'styled-components';
import _ from 'lodash';
import PropTypes from 'prop-types';
import HelpText from 'granite-admin/core/components/HelpText';
const StyledFormField = styled(FormField)`
  height: ${({
  displayName
}) => displayName === 'TextArea' ? `61px` : `100%`};
  margin-bottom: ${({
  displayName
}) => displayName === 'Styled(GMapAutocomplete)' && `0px`};
  position: relative;
  width: 100%;
  height: 100%;
  ${({
  displayName,
  error,
  theme
}) => (displayName === 'PhoneInput' || displayName === 'Select' || displayName === 'MultiSelect' || displayName === 'AsyncSelect' || displayName === 'ConfigField') && `> div[class*='ContentBox'] {
    border-color: ${!error && theme.global.colors['border']};
    &:focus-within {
      border-color: ${!error && theme.global.colors['brand']};
    }
  }`}
  > label {
    width: ${({
  displayName,
  isHovering
}) => !isHovering && (displayName === 'Select' || displayName === 'MultiSelect' || displayName === 'AsyncSelect') ? 'calc(100% - 37px)' : 'auto'};
  }
  & input {
    border: none !important;
    &:focus,
    &:hover {
      outline: none;
      box-shadow: none;
    }
  }
  input::placeholder {
    color: transparent;
  }
  & > label {
    pointer-events: ${({
  isHovering
}) => !isHovering && 'none'};
    color: ${({
  labelColor,
  labelColorError,
  error
}) => error && labelColorError ? labelColorError : labelColor || `#868686`};
    position: relative;
    z-index: 3;
    top: ${({
  displayName
}) => displayName === 'CheckBox' ? `0` : `2.5rem`};
    font-weight: ${({
  isHovering
}) => isHovering ? `bold` : `normal`};
    transform: ${({
  isHovering
}) => isHovering ? `translateY(-30px)` : `translateY(0)`};
    left: 0px;
    transition: all 0.2s ease-in-out;
    margin-top: 0px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    height: ${({
  displayName
}) => displayName === 'TextArea' ? `22px` : `auto`};
  }
  &:focus-within {
    & > label {
      font-weight: bold;
      transform: translateY(-30px);
      width: 100%;
    }
  }
`;
const OriginalFormField = styled(FormField)`
  width: 100%;
  & > label {
    margin-bottom: 0px;
    color: ${({
  labelColor,
  labelColorError,
  error
}) => error && labelColorError ? labelColorError : labelColor || `#868686`};
    font-weight: bold;
  }
`;
const TitleSpan = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  min-height: 0;
  > button {
    border: none;
  }
`;
const debounceOptions = {
  leading: true,
  trailing: false
};
const Field = ({
  label,
  name,
  error,
  children,
  flex,
  isDisabled,
  noBorder,
  normal,
  showHelpTextAsSlider,
  ...rest
}) => {
  const theme = useContext(ThemeContext);
  const {
    labelColor,
    labelColorError
  } = theme?.global.colors;
  const {
    hideAllFieldTooltip
  } = theme;
  const [mouseFocus, setMouseFocus] = useState(false);
  const [labelref, setLabelref] = useState();
  const normalField = useMemo(() => children?.type?.displayName === 'CheckBox' || children?.type?.displayName === 'RadioButtonGroup' || normal, [children, normal]);
  const handleMouseEnter = e => setMouseFocus(true);
  const handleMouseLeave = () => setMouseFocus(false);
  const debouncedHandleMouseEnter = _.debounce(handleMouseEnter, 200, debounceOptions);
  const debouncedHandleMouseLeave = _.debounce(handleMouseLeave, 200);
  const isHovering = useMemo(() => {
    // returns true - label above, label false - inside
    // const displayName = children?.type?.displayName;
    // const name = children?.type?.name;
    const value = children?.props?.value;
    const defaultSelectedOptions = children?.props?.defaultSelectedOptions;
    const defaultSelected = children?.props?.defaultSelected;
    const initialDate = children?.props?.initialDate;
    if (typeof value === 'number') return true;
    if (typeof value === 'boolean') return true;
    if (typeof initialDate === 'string' && initialDate?.length > 0) return true;
    if (defaultSelectedOptions?.length || defaultSelected?.length) return true;
    if (_.isEmpty(value)) return false;
    if (!value) return false;
    return true;
    // if ((value !== '' && value !== undefined && value !== null) || defaultSelectedOptions?.length || defaultSelected?.length) return true;
  }, [children]);
  const ref = React.useRef();
  React.useEffect(() => {
    if (ref.current && ref.current?.firstElementChild?.firstElementChild?.nodeName === 'LABEL') {
      let labelRef = ref.current?.firstElementChild?.firstElementChild;
      if (!hideAllFieldTooltip) labelRef.title = labelRef.textContent;
      setLabelref(labelRef);
    }
  }, [ref, name, hideAllFieldTooltip]);
  const childHasValue = () => {
    const value = children?.props?.value;
    const defaultSelectedOptions = children?.props?.defaultSelectedOptions;
    const defaultSelected = children?.props?.defaultSelected;
    const initialDate = children?.props?.initialDate;
    if (children?.type?.displayName?.includes?.('CurrencyInput') && value) return `$ ${(+value ?? '').toLocaleString(undefined, {
      minimumFractionDigits: 2
    })}`;
    if (typeof value === 'number') return value;
    if (typeof value === 'boolean' || typeof value === 'object' && !Array.isArray(value) || Array.isArray(value) && typeof value[0] === 'object' || value && children?.props?.type === 'password') return '';
    if (typeof initialDate === 'string' && initialDate?.length > 0) return initialDate;
    if (defaultSelectedOptions?.length || defaultSelected?.length) return '';
    if (value && children?.type?.displayName === 'Select') return '';
    if (_.isEmpty(value)) return undefined;
    if (!value) return undefined;
    return value;
  };
  return /*#__PURE__*/React.createElement(Box, _extends({
    ref: ref,
    onMouseEnter: debouncedHandleMouseEnter,
    onMouseLeave: debouncedHandleMouseLeave,
    direction: "row"
  }, rest), normalField && /*#__PURE__*/React.createElement(OriginalFormField, _extends({
    isHovering: isHovering,
    isDisabled: isDisabled,
    flex: flex,
    name: name,
    label: label,
    error: error,
    displayName: children?.type?.displayName,
    noBorder: noBorder,
    labelColor: labelColor,
    labelColorError: labelColorError
  }, rest), children), !normalField && /*#__PURE__*/React.createElement(StyledFormField, _extends({
    isHovering: isHovering,
    isDisabled: isDisabled,
    flex: flex,
    name: name,
    label: label === '""' ? null : label,
    error: error,
    displayName: children?.type?.displayName,
    noBorder: noBorder,
    labelColor: labelColor,
    labelColorError: labelColorError
  }, rest), /*#__PURE__*/React.createElement(TitleSpan, {
    title: !hideAllFieldTooltip && (childHasValue() ?? labelref?.textContent)
  }, children)), /*#__PURE__*/React.createElement(HelpText, {
    name: name,
    active: mouseFocus,
    showHelpTextAsSlider: showHelpTextAsSlider
  }));
};
Field.defaultProps = {};
Field.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  children: PropTypes.any,
  flex: PropTypes.any,
  isDisabled: PropTypes.bool,
  noBorder: PropTypes.bool,
  normal: PropTypes.bool,
  name: PropTypes.string,
  showHelpTextAsSlider: PropTypes.bool
};
export default Field;